import searchSessionService from '@/js/common/Domain/Service/search/searchSession';
import { filterByBonusMiles, filterByMirCashback } from '@features/tourProduct/filterTourProducts';
import AbTests from '@/js/common/Domain/Service/abTests';
import getStore from '@ve/services/getStore/getStore';
import { prepareTourProductsWithoutHotelAndRailway, prepareTourIdentities, getAdditionalTravelataTours } from '@/js/common/components/hotel/tourProductUtils';
import '@/css/common/components/warranted-icon.scss';

const computeds = {
    product: {
        deps: ['tour', 'hotelOffer', 'railwayTour'],
        get: (tour, hotelOffer, railwayTour) => tour || hotelOffer || railwayTour,
    },
    roomDescriptionAvailable: {
        deps: ['product'],
        get: (product) => !!product?.get('room')?.description,
    },
    roomStandardName: {
        deps: ['product'],
        get: (product) => product?.get('room')?.standardName || product?.get('room')?.name,
    },
    beds: {
        deps: ['product'],
        get: (product) => product?.get('room')?.beds?.toLowerCase() || null,
    },    
    paramsForRouter: {
        deps: ['id', 'isExclusive', 'directFlight', 'block', 'bonusMiles', 'mirCashback', 'foundedGroupWithCurrentProduct', 'coreTourData', 'tour', 'prepayments', 'groupSearch'],
        get(id, isExclusive, directFlight, block, bonusMiles, mirCashback, foundedGroupWithCurrentProduct, coreTourData, tour, prepayments, groupSearch) {
            const { preparedGroup } = foundedGroupWithCurrentProduct;
            const store = getStore();
            const filterOperators = store.state.mapForFilters.operators;
            const filterBonusMiles = store.state.mapForFilters.bonusMiles;
            const operatorIds = preparedGroup.map((tour) => tour.get('tour').get('operator').id);
            const toursPrepayments = preparedGroup.flatMap((tour) =>
                tour.get('firstPaymentDefinitions')?.map((f) => f.firstPaymentPart * 100));
            const filteredPrepayments = prepayments?.filter((p) => toursPrepayments.includes(p));

            const paramsForRouter = {
                sid: searchSessionService.getSearchId(),
                hsid: searchSessionService.getHotelSearchId(),
            };

            if (isExclusive) {
                paramsForRouter.isExclusive = true;
            }
            if(groupSearch) {
                paramsForRouter.groupSearch = true;
            }
            if (directFlight) {
                paramsForRouter.directFlight = true;
            }
            if (block) {
                paramsForRouter.block = true;
            }
            if (filterBonusMiles) {
                paramsForRouter.bonusMiles = true;
            }
            if (mirCashback) {
                paramsForRouter.mirCashback = true;
            }

            if (coreTourData.map((tour) => tour.get('id'))
                    .includes(id)
                    && tour?.get('priceWithOilTax')
                ) {
                const hotelPriceData = {
                    hotelMinPriceIdentity: id,
                    hotelMinPrice: tour.get('priceWithOilTax'),
                    hotelMinPriceOilTax: tour.get('oilTax') ?? 'null',
                }

                Object.assign(paramsForRouter, hotelPriceData);
            }
            paramsForRouter.selectedOperators = filterOperators?.filter((operator) => operatorIds.includes(operator));

            if (filteredPrepayments?.length) {
                paramsForRouter.prepayments = filteredPrepayments;
            }

            return paramsForRouter;
        },
    },
    linkRel: {
        deps: ['hasBlank'],
        get(hasBlank) {
            return hasBlank === '_blank' ? 'noopener noreferrer' : null;
        }
    },
    hasBlank: {
        get() {
            if (AbTests.hasTest('TRAV-6375')) {
                return '_blank';
            }
            return '_self';
        }
    },
    tourPageUrl: {
        deps: ['toursGroup', 'hotelOfferIdentities', 'paramsForRouter'],
        get(toursGroup, hotelOfferIdentities, paramsForRouter) {
            const hotelId = window.appConfig.hotelInfo.id;
            const identities = toursGroup.map((tour) => tour.get('id'));

            return `/hotel/${hotelId}/tourPage?${$.param({ identities, hotelOfferIdentities })}#?${$.param(paramsForRouter)}`;
        },
    },
    tourSetBackgrounMir: {
        get() {
            const tour = this.model.get('tour');
            return tour?.get('cashbackAvailable');
        },
    },
    existsOperators: {
        deps: ['similarProducts'],
        get(similarProducts) {
            if (!similarProducts) {
                return '';
            }
            const operators = _.chain([this.model, ...similarProducts])
                .map((tour) => tour.get('product')?.get('operator')?.name)
                .compact()
                .uniq()
                .value()
                .join(', ');

            return _.unescape(operators);
        },
    },
};

const events = {
    'mouseup .js-open-tour-page': 'tourSelectedOperators', // click не обрабатывает все клики (прим средняя кнопка мыши), поэтому используем mouseup
    'click .roomName': 'slideRoomDescription',
    'click .hideDescriptionElement': 'hideRoomDescription',
    'click .js-open-tour-info-by-room-name': 'openRoomInfo',
    'click .js-open-tour-info-by-room-photo': 'openRoomInfo',
    'click .js-hotel-offer-handler': 'hotelOfferHandler',
    'click .js-product-type-railway-tour': 'railwayTourHandler',
};
const bindings = {
    '.hotelTour__tour-product': 'classes:{"hotelTour__tour-product hotelTour__cashback-bg": tourSetBackgrounMir}',
    '.js-open-tour-page': 'attr:{href:tourPageUrl, target:hasBlank, rel:linkRel}',
    '.js-room-category-name': 'text: roomStandardName, classes:{"have-description": roomDescriptionAvailable}, attr:{title: roomStandardName}',
    '.js-beds': 'toggle: beds',
    '.js-beds-text': 'text: beds',
    '.js-exists-operators': 'text: existsOperators',
};

const defaults = {
    id: null,
    currentTour: null,
    tourProducts: null,
    similarProducts: null,
    product: null,
    roomDescription: null,
    beds: null,
    quoteUuid: null,
    prepayments: [],
    groupedTourProducts: [],
    block: null,
    groupSearch: false,
};

const viewModelComputeds = {
    coreTourData: {
        deps: ['tourProducts', 'currentTour', 'product'],
        get(tourProducts, currentTour, product) {
            if (!currentTour || !product) {
                return [];
            }

            const tours = [].concat(currentTour, tourProducts || []);
            return prepareTourIdentities(tours, product);
        }
    },
    foundedGroupWithCurrentProduct: {
        deps: ['beds', 'id', 'groupedTourProducts', 'product'],
        get: (beds, id, groupedTourProducts, product) => {
            const foundGroupWithCurrentProduct = groupedTourProducts
                .find((group) => group.find((elem) => {
                    if (beds) {
                        return elem.id === id
                            && group.every((element) => (element.get('tour') ? element.get('tour').get('room').beds === beds : true));
                    }
                    return elem.id === id;
                })) || [];
            const preparedGroup = prepareTourProductsWithoutHotelAndRailway(foundGroupWithCurrentProduct, product);

            return {
                foundGroupWithCurrentProduct,
                preparedGroup,
            }
        },
    },
    hotelOffers: {
        deps: ['similarProducts'],
        get: (similarProducts) => {
            return similarProducts?.filter((product) => product.get('type') === 'HotelOfferProduct') || [];
        },
    },
    hotelOfferIdentities: {
        deps: ['hotelOffers'],
        get(hotelOffers) {
            let cheapestHotelOffer = null;
            if (hotelOffers && hotelOffers.length > 0) {
                for (const offer of hotelOffers) {
                    if (!cheapestHotelOffer || offer.get('price') < cheapestHotelOffer.get('price')) {
                        cheapestHotelOffer = offer;
                    }
                }
            }

            return cheapestHotelOffer ? [cheapestHotelOffer.get('id')] : [];
        },
    },
    toursGroup: {
        deps: ['foundedGroupWithCurrentProduct', 'coreTourData'],
        get(foundedGroupWithCurrentProduct, coreTourData) {
            const { foundGroupWithCurrentProduct, preparedGroup } = foundedGroupWithCurrentProduct;
            const result = [...preparedGroup];
            const identityGroup = preparedGroup.map((tour) => tour.get('id'));
            const additionalTravelataTours = getAdditionalTravelataTours(foundGroupWithCurrentProduct, identityGroup);

            if (additionalTravelataTours.length > 0) {
                result.push(...additionalTravelataTours);
            }

            return result?.length
                ? result
                : coreTourData;
        }
    },
}

export {
    events,
    bindings,
    defaults,
    computeds,
    viewModelComputeds,
};